import {useEffect, useState, useRef, useCallback} from 'react';
import {
  initializeCornerstone,
  getSharedRenderingEngine,
} from './cornerstoneInit';
import {
  createDicomError,
  type DicomError,
  DicomInitializationError,
} from './dicomErrors';

type UseDicomViewerState = {
  isLoading: boolean;
  error: DicomError | null;
  isReady: boolean;
};

export type UseDicomViewerReturn = {
  retryLoad: () => void;
} & UseDicomViewerState;

export function useDicomViewer(
  fileUrl: string,
  elementId: string
): UseDicomViewerReturn {
  const [state, setState] = useState<UseDicomViewerState>({
    isLoading: true,
    error: null,
    isReady: false,
  });

  const viewportIdRef = useRef<string | null>(null);

  const loadImage = useCallback(async () => {
    try {
      const renderingEngine = await initializeCornerstone();

      // Import Enums dynamically to keep Cornerstone out of main bundle
      const {Enums} = await import('@cornerstonejs/core');

      const element = document.getElementById(elementId) as HTMLDivElement;
      if (!element) {
        throw new DicomInitializationError(
          'DICOM viewer element not found in DOM'
        );
      }

      const currentViewportId = viewportIdRef.current;
      if (currentViewportId) {
        try {
          renderingEngine.disableElement(currentViewportId);
        } catch {
          // Ignore cleanup errors
        }
      }

      const viewportId = `viewport-${elementId}-${Date.now()}`;
      viewportIdRef.current = viewportId;

      const viewportInput = {
        viewportId,
        type: Enums.ViewportType.STACK,
        element,
        defaultOptions: {
          background: [0, 0, 0] as [number, number, number],
        },
      };

      renderingEngine.enableElement(viewportInput);

      // Get the viewport (cast to StackViewport for setStack method)
      const viewport = renderingEngine.getViewport(viewportId) as any;

      const imageId = `wadouri:${fileUrl}`;

      await viewport.setStack([imageId]);
      renderingEngine.render();

      setState({
        isLoading: false,
        error: null,
        isReady: true,
      });
    } catch (caughtError) {
      setState({
        isLoading: false,
        error: createDicomError(caughtError),
        isReady: false,
      });
    }
  }, [elementId, fileUrl]);

  const retryLoad = () => {
    setState(previous => ({
      ...previous,
      isLoading: true,
      error: null,
    }));
    loadImage();
  };

  useEffect(() => {
    if (fileUrl && elementId) {
      loadImage();
    }

    // Cleanup function - only disable the viewport, not the shared engine
    return () => {
      const cleanup = () => {
        try {
          const renderingEngine = getSharedRenderingEngine();
          const currentViewportId = viewportIdRef.current;

          if (renderingEngine && currentViewportId) {
            renderingEngine.disableElement(currentViewportId);
            viewportIdRef.current = null;
          }
        } catch {
          // Ignore cleanup errors
        }
      };

      cleanup();
    };
  }, [fileUrl, elementId, loadImage]);

  return {
    ...state,
    retryLoad,
  };
}
